// Display · entityLinkContext — renderer contract, context object, hook.
//
// Split out of `EntityLinkContext.tsx` so that module exports components only.

import { createContext, useContext, type ReactNode } from "react";
import { EntityChip } from "@ds/display/EntityChip";

// ─── Renderer props (minimal contract shared by all tiers) ─────────────────

export interface EntityLinkRendererProps {
  /** Entity kind string (matches EntityType in connected). */
  type: string;
  /** Entity numeric primary key. */
  id: number;
  /** Display label. */
  label: ReactNode;
  /**
   * Visual variant.
   * - `"inline"` (default) — tinted link text.
   * - `"pill"` — chip chrome (icon · label · arrow).
   */
  variant?: "pill" | "inline";
  /** Size scale — only applies in pill/chip variant. Default `md`. */
  size?: "sm" | "md";
  /** `data-testid` forwarded to the root element. */
  testId?: string;
}

// ─── Context ─────────────────────────────────────────────────────────────────

export type EntityLinkRenderer = (props: EntityLinkRendererProps) => ReactNode;

/** Pure-visual fallback — renders `EntityChip` without navigation or popover. */
function defaultRenderer({
  label,
  variant,
  size,
  testId,
}: EntityLinkRendererProps): ReactNode {
  const chipVariant: "chip" | "inline" = variant === "pill" ? "chip" : "inline";
  return (
    <EntityChip
      label={label}
      variant={chipVariant}
      tone="blue"
      arrow
      size={size}
      testId={testId}
    />
  );
}

export const EntityLinkContext = createContext<EntityLinkRenderer>(defaultRenderer);

// ─── Consumer hook ────────────────────────────────────────────────────────────

/**
 * Returns the current entity-link renderer.
 *
 * ```tsx
 * const renderLink = useEntityLinkRenderer();
 * return renderLink({ type: "company", id: 42, label: "Acme" });
 * ```
 */
export function useEntityLinkRenderer(): EntityLinkRenderer {
  return useContext(EntityLinkContext);
}
